import { useState, useEffect } from "react";
import { Copy, Check, Heart, ExternalLink, Bookmark, Ticket, Printer, Store as StoreIcon, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { redeemCoupon, addFavorite, removeFavorite, voteCoupon, logClick } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import StoreLogo from "@/components/StoreLogo";
import ShareMenu from "@/components/ShareMenu";
import WatchStoreButton from "@/components/WatchStoreButton";
import { addToCopyHistory } from "@/lib/copyHistory";
import { getConfig, wrapAffiliate } from "@/lib/affiliate";

const catColor = {
  Electronics: "var(--cat-electronics)",
  Fashion: "var(--cat-fashion)",
  Grocery: "var(--cat-grocery)",
  Food: "var(--cat-food)",
  Home: "var(--cat-home)",
  Travel: "var(--cat-travel)",
  Beauty: "var(--cat-beauty)",
  Online: "var(--cat-online)",
  Health: "var(--cat-health)",
  Pets: "var(--cat-pets)",
  Retail: "var(--cat-retail)",
};

function catBg(cat) {
  return catColor[cat] || "var(--cat-default)";
}

const kindMeta = {
  code: { label: "PROMO CODE", Icon: Ticket },
  deal: { label: "DEAL", Icon: Bookmark },
  printable: { label: "PRINTABLE", Icon: Printer },
  "in-store": { label: "IN-STORE", Icon: StoreIcon },
};

const sourceMeta = {
  curated: { label: "CURATED", color: "bg-[#1a1a19] text-[#f6f4f0]" },
  reddit: { label: "REDDIT", color: "bg-[#ff4500] text-white" },
  slickdeals: { label: "SLICKDEALS", color: "bg-[#003366] text-white" },
  community: { label: "COMMUNITY", color: "bg-[#5c5c5c] text-white" },
  ozbargain: { label: "OZBARGAIN", color: "bg-[#e35a00] text-white" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const now = new Date();
  const d = new Date(iso);
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function CouponCard({ coupon, index = 0 }) {
  const { user, favIds, refreshFavs, openLogin } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [voteState, setVoteState] = useState({
    up: coupon.upvotes || 0,
    down: coupon.downvotes || 0,
    my: null,
  });
  const isFav = favIds?.has(coupon.id);
  const kind = kindMeta[coupon.kind] || kindMeta.code;
  const KindIcon = kind.Icon;
  const src = sourceMeta[coupon.source] || sourceMeta.curated;
  const daysLeft = daysUntil(coupon.expires_at);

  const handleReveal = async () => {
    setRevealed(true);
    if (coupon.code) {
      try {
        await navigator.clipboard.writeText(coupon.code);
        setCopied(true);
        toast.success(`Code ${coupon.code} copied`, { duration: 2000 });
        setTimeout(() => setCopied(false), 2200);
      } catch {
        toast.info(`Code: ${coupon.code}`);
      }
    }
    redeemCoupon(coupon.id).catch(() => {});
  };

  const handleFav = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save deals", {
        action: { label: "Sign in", onClick: () => openLogin("signin") },
      });
      return;
    }
    if (isFav) {
      await removeFavorite(coupon.id);
      toast.success("Removed from saved");
    } else {
      await addFavorite(coupon.id);
      toast.success("Saved");
    }
    refreshFavs();
  };

  const handleOpen = async () => {
    let target = coupon.store_url;
    try {
      const cfg = await getConfig();
      target = wrapAffiliate(coupon.store_url, cfg);
    } catch {}
    // Log outbound click for CTR tracking (fire-and-forget)
    logClick(coupon.id);
    if (target) window.open(target, "_blank", "noopener");
    redeemCoupon(coupon.id).catch(() => {});
  };

  const castVote = async (dir) => {
    // optimistic UI
    setVoteState((v) => {
      let up = v.up;
      let down = v.down;
      if (v.my === dir) return v; // same, no-op
      if (v.my === "up") up -= 1;
      if (v.my === "down") down -= 1;
      if (dir === "up") up += 1;
      else down += 1;
      return { up, down, my: dir };
    });
    try {
      const r = await voteCoupon(coupon.id, dir);
      if (r && typeof r.upvotes === "number") {
        setVoteState((v) => ({ ...v, up: r.upvotes, down: r.downvotes }));
      }
    } catch {
      toast.error("Couldn't record vote");
    }
  };

  const tilt = index % 3 === 0 ? "hover:rotate-[0.4deg]" : index % 3 === 1 ? "hover:-rotate-[0.4deg]" : "hover:rotate-[0.1deg]";

  return (
    <article
      data-testid={`coupon-card-${coupon.id}`}
      className={`coupon-card p-5 md:p-6 flex flex-col justify-between relative group ${tilt} ${coupon.is_sponsored ? "sponsored-card" : ""}`}
      style={{ minHeight: 260 }}
    >
      {coupon.is_sponsored && (
        <div
          data-testid={`sponsored-ribbon-${coupon.id}`}
          className="absolute -top-3 -left-3 z-10 rotate-[-8deg]"
        >
          <div
            className="mono text-[10px] tracking-[0.3em] font-black px-3 py-1 border-2 border-[#2d2a32] shadow-mustard"
            style={{
              background: "linear-gradient(135deg,#f7c873 0%,#f2a93b 100%)",
              color: "#2d2a32",
              borderRadius: 6,
            }}
          >
            ★ SPONSORED
          </div>
        </div>
      )}
      {/* Store banner — the big brand line */}
      <div
        className="flex items-start justify-between gap-3 pb-3 mb-3 border-b-2 border-dashed border-[#2d2a32]/40"
        data-testid={`coupon-storebanner-${coupon.id}`}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <StoreLogo
            store={coupon.store}
            storeUrl={coupon.store_url}
            size={52}
            testid={`store-logo-${coupon.id}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[9px] tracking-[0.25em] text-[#5c5c5c] mb-1 flex-wrap">
              <KindIcon className="w-3 h-3" />
              <span>{kind.label}</span>
              <span>·</span>
              <span>{coupon.category?.toUpperCase()}</span>
              <span
                className={`ml-1 px-1.5 py-0.5 text-[9px] tracking-[0.2em] ${src.color}`}
                data-testid={`source-badge-${coupon.id}`}
              >
                {src.label}
              </span>
            </div>
            <div
              className="serif text-xl sm:text-2xl md:text-3xl leading-tight tracking-tight font-semibold cursor-pointer hover:text-[#ff6b6b] break-normal hyphens-none"
              onClick={() => navigate(`/coupon/${coupon.id}`)}
              data-testid={`coupon-store-${coupon.id}`}
              title={coupon.store}
            >
              {coupon.store}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <WatchStoreButton store={coupon.store} variant="compact" />
          <ShareMenu coupon={coupon} />
          <button
            onClick={handleFav}
            data-testid={`fav-btn-${coupon.id}`}
            aria-label="Save coupon"
            className={`w-10 h-10 border-2 border-[#2d2a32] rounded-full flex items-center justify-center wiggle-on-hover ${
              isFav ? "bg-[#ff6b6b] text-white" : "bg-white hover:bg-[#ff6b6b] hover:text-white"
            }`}
          >
            <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
          </button>
        </div>
      </div>

      {/* Discount + title */}
      <div className="mb-3">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span
            className="inline-block px-3 py-1 text-xs tracking-[0.18em] font-bold text-[#2d2a32] border-2 border-[#2d2a32] -rotate-1"
            style={{ background: catBg(coupon.category), borderRadius: "6px" }}
            data-testid={`coupon-discount-${coupon.id}`}
          >
            {coupon.discount_label}
          </span>
          {(() => {
            const created = coupon.created_at ? new Date(coupon.created_at) : null;
            if (!created) return null;
            const hours = (Date.now() - created.getTime()) / 36e5;
            if (hours < 24) {
              return (
                <span
                  data-testid={`freshness-badge-${coupon.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] font-black text-white bg-[#ff5b48] border-2 border-[#2d2a32] rounded-md"
                  title="Added in the last 24 hours"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  NEW
                </span>
              );
            }
            if (hours < 24 * 7) {
              return (
                <span
                  data-testid={`freshness-badge-${coupon.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] font-black text-[#2d2a32] bg-[#f7c873] border-2 border-[#2d2a32] rounded-md"
                  title="Verified this week"
                >
                  ✓ VERIFIED
                </span>
              );
            }
            return null;
          })()}
          {(() => {
            // Community work-rate badge — only surfaces once there's real signal (3+ votes)
            // so we don't scare users off a brand-new code that no one has tried yet.
            const up = voteState.up || 0;
            const down = voteState.down || 0;
            const total = up + down;
            if (total < 3) return null;
            const pct = Math.round((up / total) * 100);
            const cls =
              pct >= 80
                ? "bg-[#7dbb6e] text-white border-[#2d2a32]"
                : pct >= 50
                  ? "bg-[#f7c873] text-[#2d2a32] border-[#2d2a32]"
                  : "bg-[#5c5c5c] text-white border-[#2d2a32]";
            const label = pct >= 80 ? "WORKS" : pct >= 50 ? "MIXED" : "STALE";
            return (
              <span
                data-testid={`worksrate-badge-${coupon.id}`}
                className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.18em] font-black border-2 rounded-md ${cls}`}
                title={`${up} of ${total} shoppers said this code worked`}
              >
                {pct}% {label}
              </span>
            );
          })()}
        </div>
        <h3
          className="text-sm md:text-base leading-snug font-semibold text-[#2d2a32] cursor-pointer hover:underline decoration-2 underline-offset-4"
          onClick={() => navigate(`/coupon/${coupon.id}`)}
          data-testid={`coupon-title-${coupon.id}`}
        >
          {coupon.title}
        </h3>
      </div>

      {/* Description */}
      <p className="text-sm md:text-[15px] text-[#3a3a38] leading-relaxed mb-4 line-clamp-3 font-medium">
        {coupon.description}
      </p>

      {/* Divider */}
      <div className="cut-divider my-2" />

      {/* Code + CTA */}
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="flex-1 min-w-0">
          {coupon.code ? (
            <button
              onClick={handleReveal}
              data-testid={`reveal-code-${coupon.id}`}
              className="w-full text-left group/code"
              aria-label="Reveal promo code"
            >
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-1">
                {revealed ? "COPIED" : "TAP TO REVEAL"}
              </div>
              <div className="border-2 border-dashed border-[#2d2a32] bg-[#fff8ef] rounded-lg px-3 py-2 flex items-center justify-between hover:bg-[#2d2a32] hover:text-[#fff8ef] hover:border-solid">
                <span className="mono text-lg tracking-widest font-semibold truncate">
                  {revealed ? coupon.code : "•••••••"}
                </span>
                {copied ? (
                  <Check className="w-4 h-4 shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 shrink-0" />
                )}
              </div>
            </button>
          ) : (
            <div>
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-1">
                NO CODE NEEDED
              </div>
              <div className="border-2 border-[#2d2a32] rounded-lg px-3 py-2 bg-[#fff8ef] text-sm tracking-wide">
                Auto-applied at checkout ✨
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between gap-3 text-[10px] tracking-[0.15em] text-[#5c5c5c] flex-wrap">
        <span
          data-testid={`coupon-expiry-${coupon.id}`}
          className={`inline-flex items-center px-2.5 py-1 text-xs md:text-sm font-bold tracking-[0.14em] border-2 border-[#2d2a32] text-[#2d2a32] rounded-md ${
            daysLeft === null
              ? "bg-white"
              : daysLeft > 0
              ? "bg-[#ff6b6b]"
              : "bg-[#2d2a32] text-white border-[#2d2a32]"
          }`}
        >
          {daysLeft !== null
            ? daysLeft > 0
              ? `EXPIRES IN ${daysLeft}D`
              : "EXPIRED"
            : "NO EXPIRATION"}
        </span>
        <button
          onClick={handleOpen}
          data-testid={`open-store-${coupon.id}`}
          className="flex items-center gap-1 hover:text-black hover:underline underline-offset-2 text-right whitespace-nowrap overflow-hidden text-ellipsis max-w-[60%]"
        >
          VISIT {coupon.store.toUpperCase()}
          <ExternalLink className="w-3 h-3 shrink-0" />
        </button>
      </div>

      {/* Vote bar — bolder CTA drives more community-verification data */}
      <div className="mt-3 pt-3 border-t border-dashed border-[#2d2a32]/30 flex items-center justify-between gap-3 text-[10px] tracking-[0.15em] text-[#5c5c5c]">
        <span className="font-semibold text-[#2d2a32]">Did it work?</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => castVote("up")}
            data-testid={`vote-up-${coupon.id}`}
            className={`flex items-center gap-1 px-2 py-1 border-2 rounded-full text-[11px] font-bold ${
              voteState.my === "up"
                ? "border-[#7dbb6e] bg-[#7dbb6e] text-white"
                : "border-[#2d2a32]/40 hover:border-[#7dbb6e] hover:text-[#7dbb6e]"
            }`}
            aria-label="Vote up"
          >
            <ThumbsUp className="w-3 h-3" />
            {voteState.up || 0}
          </button>
          <button
            onClick={() => castVote("down")}
            data-testid={`vote-down-${coupon.id}`}
            className={`flex items-center gap-1 px-2 py-1 border-2 rounded-full text-[11px] font-bold ${
              voteState.my === "down"
                ? "border-[#ff6b6b] bg-[#ff6b6b] text-white"
                : "border-[#2d2a32]/40 hover:border-[#ff6b6b] hover:text-[#ff6b6b]"
            }`}
            aria-label="Vote down"
          >
            <ThumbsDown className="w-3 h-3" />
            {voteState.down || 0}
          </button>
        </div>
      </div>

      {/* Verified stamp */}
      {coupon.verified && (
        <div className="absolute top-3 right-14 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <div className="border border-[#D34836] text-[#D34836] px-1.5 py-0.5 text-[9px] tracking-[0.2em] -rotate-6">
            VERIFIED
          </div>
        </div>
      )}
    </article>
  );
}
